import { Badge } from '@/components/ui/badge'
import { Progress } from '@/components/ui/progress'
import { cn } from '@/utils/cn'

interface UsageMeterProps {
  label: string
  current: number
  limit: number
  showPercentage?: boolean
  /** Formats numeric values for display (e.g. bytes → "1.4 MB"). */
  formatValue?: (value: number) => string
  /** Overrides the rendered limit (e.g. "Unlimited"); defaults to the formatted limit. */
  limitLabel?: string
}

export function UsageMeter({
  label,
  current,
  limit,
  showPercentage = true,
  formatValue = (value) => String(value),
  limitLabel,
}: UsageMeterProps) {
  const isUnlimited = !Number.isFinite(limit)
  const percentage = !isUnlimited && limit > 0 ? Math.min((current / limit) * 100, 100) : 0
  const isNearLimit = !isUnlimited && percentage >= 80 && percentage < 100
  const isAtLimit = !isUnlimited && percentage >= 100

  return (
    <div className="space-y-2.5 rounded-xl border border-border/70 bg-card p-4 transition-all duration-200 hover:border-border hover:shadow-2xs">
      <div className="flex flex-wrap items-center justify-between gap-2">
        {/* Label & Status Badge */}
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-foreground">{label}</span>
          {isAtLimit && (
            <Badge variant="destructive" className="h-5 px-1.5 text-[10px]">
              Limit Reached
            </Badge>
          )}
          {isNearLimit && (
            <Badge variant="warning" className="h-5 px-1.5 text-[10px]">
              Near Limit
            </Badge>
          )}
          {isUnlimited && (
            <Badge variant="secondary" className="h-5 px-1.5 text-[10px] font-normal text-muted-foreground">
              Unlimited
            </Badge>
          )}
        </div>

        {/* Polished Value & Percentage Readout */}
        <div className="flex items-center gap-2">
          <div className="flex items-baseline gap-1 text-xs tabular-nums">
            <span
              className={cn(
                'text-sm font-bold',
                isAtLimit
                  ? 'text-destructive'
                  : isNearLimit
                    ? 'text-warning-600 dark:text-warning-400'
                    : 'text-foreground'
              )}
            >
              {formatValue(current)}
            </span>
            <span className="text-xs text-muted-foreground/60">/</span>
            <span className="text-xs font-medium text-muted-foreground">
              {isUnlimited ? 'Unlimited' : limitLabel ?? formatValue(limit)}
            </span>
          </div>

          {showPercentage && !isUnlimited && (
            <span
              className={cn(
                'inline-flex items-center rounded-md px-1.5 py-0.5 text-[11px] font-semibold tabular-nums',
                isAtLimit
                  ? 'bg-destructive/10 text-destructive'
                  : isNearLimit
                    ? 'bg-warning-500/15 text-warning-700 dark:text-warning-300'
                    : 'bg-muted text-muted-foreground'
              )}
            >
              {Math.round(percentage)}%
            </span>
          )}
        </div>
      </div>

      <Progress
        value={isUnlimited ? 0 : percentage}
        className={cn(
          'h-2 bg-muted transition-all',
          isAtLimit && '[&>div]:bg-destructive',
          isNearLimit && '[&>div]:bg-warning-500',
          !isAtLimit && !isNearLimit && '[&>div]:bg-primary'
        )}
      />
    </div>
  )
}